import { useState, useEffect, useCallback } from 'react';

interface UseSwitchToggleProps {
  isDisabled?: boolean;
  externalValue?: boolean;
  onToggle?: (isOn: boolean) => void;
  shouldSyncWithExternalValue?: boolean;
}

export function useSwitchToggle({
  externalValue,
  shouldSyncWithExternalValue,
  isDisabled = false,
  onToggle,
}: UseSwitchToggleProps = {}) {
  const [isOn, setIsOn] = useState<boolean | undefined>(externalValue);

  useEffect(() => {
    if (shouldSyncWithExternalValue) setIsOn(externalValue);
  }, [shouldSyncWithExternalValue, externalValue]);

  const toggleSwitch = useCallback(() => {
    if (!isDisabled) {
      setIsOn((prev) => {
        onToggle?.(!prev);
        return !prev;
      });
    }
  }, [isDisabled, onToggle]);

  const resetSwitch = useCallback(() => {
    setIsOn(externalValue);
  }, [externalValue]);

  const setSwitch = useCallback(
    (newValue: boolean) => {
      if (!isDisabled) {
        setIsOn((prev) => {
          if (prev === newValue) return prev;
          onToggle?.(newValue);
          return newValue;
        });
      }
    },
    [isDisabled, onToggle]
  );

  return {
    isOn: !!isOn,
    toggleSwitch,
    resetSwitch,
    setSwitch,
    isDisabled,
  };
}
